import { Controller, FieldValues, Path, ControllerProps as RhfControllerProps } from 'react-hook-form';

import { FieldDateRangePicker, FieldDateRangePickerProps } from './FieldDateRangePicker';
import { createDateRangeValidator, DateRangeValue } from './internal/validators';

const EMPTY: DateRangeValue = { from: null, to: null };

/** Maps a form's range shape, e.g. `{ start, end }`, to `{ from, to }`, which the validators then check. */
export interface DateRangeValueAdapter<TStored> {
    toView: (stored: TStored | null | undefined) => DateRangeValue;
    fromView: (view: DateRangeValue) => TStored;
}

export interface FieldDateRangePickerControlledProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
    TStored = DateRangeValue,
> extends Omit<RhfControllerProps<TFieldValues, TName>, 'render' | 'rules' | 'defaultValues' | 'defaultValue'> {
    FieldProps?: Omit<FieldDateRangePickerProps, 'value' | 'onChange' | 'onCommit' | 'name'>;
    required?: boolean;
    minDate?: string;
    maxDate?: string;
    minDurationDays?: number;
    maxDurationDays?: number;
    /** Valid only with both sides filled or neither, for an optional but coupled window. */
    requireBothOrNeither?: boolean;
    /** Custom validation, run against the **view** shape (`DateRangeValue`). */
    validate?: (range: DateRangeValue) => string | undefined;
    /** Needed when the form doesn't store a `DateRangeValue`; identity by default. */
    valueAdapter?: DateRangeValueAdapter<TStored>;
}

const identityAdapter: DateRangeValueAdapter<DateRangeValue> = {
    toView: (stored) => stored ?? EMPTY,
    fromView: (view) => view,
};

/** Uses RHF's `Controller`, not `FieldController`, whose string-based `createDateValidator` can't take a range. */
export function FieldDateRangePickerControlled<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
    TStored = DateRangeValue,
>({
    name,
    control,
    required,
    minDate,
    maxDate,
    minDurationDays,
    maxDurationDays,
    requireBothOrNeither,
    validate,
    valueAdapter,
    FieldProps,
    ...rest
}: FieldDateRangePickerControlledProps<TFieldValues, TName, TStored>): JSX.Element {
    const adapter = valueAdapter ?? (identityAdapter as unknown as DateRangeValueAdapter<TStored>);

    const rangeValidator = createDateRangeValidator({
        required,
        minDate,
        maxDate,
        minDurationDays,
        maxDurationDays,
        requireBothOrNeither,
        validate,
    });

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                validate: (value: unknown) => rangeValidator(adapter.toView(value as TStored | null | undefined)),
            }}
            render={({ field, fieldState }) => {
                const view = adapter.toView(field.value as TStored | null | undefined);
                const errorMessage = fieldState.error?.message;
                return (
                    <FieldDateRangePicker
                        {...FieldProps}
                        name={field.name}
                        inputRef={field.ref}
                        value={view}
                        onChange={(next) => field.onChange(adapter.fromView(next))}
                        onCommit={() => field.onBlur()}
                        error={Boolean(errorMessage)}
                        helperText={errorMessage ?? FieldProps?.helperText}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                );
            }}
            {...rest}
        />
    );
}
